import cx from "classnames"
import { forwardRef } from "react"
import Button, { type ButtonProps, type LinkProps } from "../ButtonComponent"
import {
  type ButtonTemplatePropsType,
  getButtonCommonClassNames,
  getButtonContent,
} from "../helpers"

type DefaultCommonPropsType = ButtonTemplatePropsType<{
  primary?: boolean
  destructive?: boolean
  selected?: boolean
}>

export type DefaultButtonPropsType = DefaultCommonPropsType & ButtonProps
export type DefaultLinkPropsType = DefaultCommonPropsType & LinkProps

const getComputedClassName = ({
  className,
  selected,
  primary,
  destructive,
}: DefaultCommonPropsType) =>
  cx(className, "cob-Button__default", {
    "cob-Button__default--selected": selected,
    "cob-Button__default--primary": primary,
    "cob-Button__default--destructive": destructive,
  })

const _DefaultButton = forwardRef<HTMLButtonElement, DefaultButtonPropsType>(
  (props: DefaultButtonPropsType, ref) => {
    const {
      children,
      selected,
      primary,
      destructive,
      icon,
      iconPosition,
      className,
      loading,
      disabled,
      rounded,
      size,
      fullWidth,
      ...restButtonProps
    } = props
    const content = getButtonContent({ children, icon, iconPosition })
    const isDisabled = disabled || loading
    const buttonClassNames = getButtonCommonClassNames({
      disabled: isDisabled,
      rounded,
      size,
      fullWidth,
      className,
    })
    return (
      <Button
        {...restButtonProps}
        className={getComputedClassName({
          className: buttonClassNames,
          selected,
          primary,
          destructive,
        })}
        ref={ref}
        loading={loading}
        disabled={isDisabled}
      >
        {content}
      </Button>
    )
  },
)
_DefaultButton.displayName = "DefaultButton"

const DefaultLink = forwardRef<HTMLAnchorElement, DefaultLinkPropsType>(
  (props: DefaultLinkPropsType, ref) => {
    const {
      children,
      selected,
      primary,
      destructive,
      icon,
      iconPosition,
      className,
      loading,
      disabled,
      rounded,
      size,
      fullWidth,
      ...restLinkProps
    } = props
    const content = getButtonContent({ children, icon, iconPosition })
    const isDisabled = disabled || loading
    const buttonClassNames = getButtonCommonClassNames({
      disabled: isDisabled,
      rounded,
      size,
      fullWidth,
      className,
    })
    return (
      <Button.Link
        {...restLinkProps}
        className={getComputedClassName({
          className: buttonClassNames,
          selected,
          primary,
          destructive,
        })}
        ref={ref}
        loading={loading}
        disabled={isDisabled}
      >
        {content}
      </Button.Link>
    )
  },
)
DefaultLink.displayName = "DefaultLink"

const DefaultButton = Object.assign(_DefaultButton, { Link: DefaultLink })

export default DefaultButton
